// Subpagina's = de horizontale tab-menu's binnen een module. Per gebruiker in te
// stellen welke tabs zichtbaar zijn.
//
// SPIEGELT backend/src/config/subpages.js — id's, volgorde en de `standaard`-vlag
// moeten daar identiek zijn, anders slaat het formulier iets op wat de server
// wegfiltert (of omgekeerd).
//
// Id-vorm: `<moduleId>:<tabId>`. De tabId's zijn LETTERLIJK de ids die de
// tabbalken in App.jsx gebruiken (MP_TABS, c360Tabs, ordersTab, invoicesTab,
// portfolioTabs) — hernoem je daar een tab, hernoem 'm dan hier en in de backend
// mee, anders verdwijnt hij stil uit ieders menu.
//
// `standaard: true` = hoort bij de basisset die een nieuwe niet-admin meekrijgt.
// Een NIEUWE subpagina krijgt bewust `standaard: false`: admin en
// ET-dossierbeheerder zien 'm meteen (die hebben altijd alles), elke andere rol
// pas nadat een beheerder 'm expliciet aanvinkt.
export const SUBPAGE_CATALOG = [
  {
    module: 'market',
    label: 'Market Prices',
    pages: [
      { id: 'actueel', label: 'Actuele prijzen', standaard: true },
      { id: 'forward', label: 'Forward', standaard: true },
      { id: 'live', label: 'Live onbalans', standaard: true },
      { id: 'profiel', label: 'Dagprofiel', standaard: true },
      // Nieuw (17-08-2026): gas day-ahead (TTF/ZTP). Bewust `standaard: false` —
      // admin en ET-dossierbeheerder zien de tab meteen, elke andere rol pas
      // nadat een beheerder hem toekent.
      { id: 'gas', label: 'Gasprijzen', standaard: false },
    ],
  },
  {
    module: 'customer360',
    label: 'Customer 360°',
    // De Metering-tab is hier instelbaar, maar staat BOVENOP de modulegrens: hij
    // verschijnt alleen als de gebruiker óók de Metering-MODULE heeft. Zet je 'm
    // hier aan zonder die module, dan blijft de tab weg — Customer 360 mag geen
    // achterdeur zijn naar een ingetrokken module.
    pages: [
      { id: 'samenvatting', label: 'Samenvatting', standaard: true },
      { id: 'orders', label: 'Orders', standaard: true },
      { id: 'facturen', label: 'Facturen', standaard: true },
      { id: 'metering', label: 'Metering', standaard: true, vereistModule: 'metering' },
      { id: 'billshock', label: 'Bill Shock', standaard: true },
      { id: 'debtcollection', label: 'Debt Collection', standaard: true },
      { id: 'paymentplans', label: 'Payment Plans', standaard: true },
      // Nieuw (07-09-2026): het blok "Helpdesk tickets" op de Samenvatting — geen
      // tab maar een blok, per gebruiker aan/uit. Standaard AAN voor master en
      // teamleader, UIT voor een verkoper; admin/ET altijd. Bovenop dit vinkje
      // blijft het leesrecht op de Helpdesk vereist (`vereistHelpdeskRecht`): het
      // formulier toont dan "leesrecht uit" zolang dat recht ontbreekt.
      { id: 'tickets', label: 'Helpdesk tickets', standaard: false, standaardRollen: ['master', 'teamleader'], vereistHelpdeskRecht: 'read' },
    ],
  },
  {
    module: 'orders',
    label: 'Orders',
    pages: [
      { id: 'orders', label: 'Orders', standaard: true },
      { id: 'fases', label: 'Fases', standaard: true },
      // Leads: zie src/leads.js. Nieuwe tab → niet standaard aan (admin/ET altijd).
      { id: 'leads', label: 'Leads', standaard: false },
    ],
  },
  {
    module: 'invoices',
    label: 'Invoices',
    pages: [
      { id: 'facturen', label: 'Facturen', standaard: true },
      { id: 'analyse', label: 'Analyse', standaard: true },
    ],
  },
  {
    module: 'revenue',
    label: 'Revenue',
    // Alleen de "commissies"-tabs zijn per gebruiker in te stellen; de overige
    // Revenue-tabs staan hier bewust NIET en volgen dus gewoon de module.
    //
    // `energielabel` = deze tab BESTAAT alleen bij dat ene energielabel. Hij is
    // dus enkel toe te kennen aan een gebruiker die op dat label zit: het
    // gebruikersformulier toont hem niet bij een ander label (zie
    // SubPageAccessPicker) en de server weert hem bij het opslaan. De labelnamen
    // zijn LETTERLIJK die uit CUSTOM_FEE_PAGINAS in App.jsx.
    //
    // Alle drie staan voor ELKE rol standaard uit; enkel admin en
    // ET-dossierbeheerder zien ze altijd.
    pages: [
      { id: 'commissies-servolt', label: 'Commissies Servolt', standaard: false, energielabel: 'Servolt Energie' },
      { id: 'commissies-evident', label: 'Commissies Evident', standaard: false, energielabel: 'Evident Energie' },
      { id: 'commissie-overzicht-evident', label: 'Commissie overzicht (Evident)', standaard: false, energielabel: 'Evident Energie' },
      // 07-09-2026: Betalingen — de betaalde partnervergoeding instellen en bekijken.
      // Standaard AAN voor master en teamleader (standaardRollen), UIT voor een
      // verkoper (die ziet enkel wat hem is toegewezen, en alleen als een beheerder
      // de tab aanvinkt); admin/ET zien 'm altijd. Backfill in server.js.
      { id: 'betalingen', label: 'Betalingen', standaard: false, standaardRollen: ['master', 'teamleader'] },
    ],
  },
  {
    module: 'helpdesk',
    label: 'Helpdesk',
    // 08-09-2026: de Helpdesk kreeg een tabbalk. "Samenvatting" is de bestaande
    // pagina (basisset, plus backfill in server.js voor bestaande gebruikers).
    // "Analyse" bestaat ALLEEN voor admin en ET-dossierbeheerder (`alleenRollen`):
    // ze vergelijkt energielabels onderling en toont Energy Together-cijfers, dus
    // ze is voor geen enkele partnerrol toe te kennen — het formulier toont ze
    // daar niet en de server weert ze; de route zelf eist bovendien die rol.
    pages: [
      { id: 'samenvatting', label: 'Overzicht', standaard: true },
      { id: 'analyse', label: 'Analyse', standaard: false, alleenRollen: ['admin', 'et_dossier'] },
      // 15-09-2026: de kennisbank met werkinstructies. Nieuwe tab = standaard uit
      // (conventie); admin/ET zien hem meteen, partnerrollen na aanvinken.
      { id: 'kennisbank', label: 'Kennisbank', standaard: false },
      // 27-09-2026: AI-voorstellen — wat de voorstellen opleveren en de hefbomen om
      // ze bij te sturen (vaste instructies, leren uit gebruik). Interne kennis:
      // alleen admin en ET-dossierbeheerder, voor geen enkele partnerrol toe te
      // kennen. De routes (/api/helpdesk-ai) eisen die rol zelf ook.
      { id: 'ai', label: 'AI-voorstellen', standaard: false, alleenRollen: ['admin', 'et_dossier'] },
      // 16-09-2026: Admin — automatische regels (trefwoord → tag → behandelaar)
      // en het tagbeheer. Enkel admin (HelpdeskPage filtert de tab er zelf uit
      // voor elke andere rol; subPageSetForUser laat full-access rollen alles zien).
      { id: 'admin', label: 'Admin', standaard: false, alleenRollen: ['admin'] },
    ],
  },
  {
    module: 'sales',
    label: 'Sales',
    pages: [
      { id: 'prestaties', label: 'Prestaties', standaard: false },
    ],
  },
  {
    module: 'portfolio',
    label: 'Portfolio Insights',
    pages: [
      { id: 'evolutie', label: 'Samenvatting', standaard: true },
      { id: 'drops', label: 'Drops', standaard: true },
      { id: 'churn', label: 'Churn', standaard: true },
      { id: 'productwissels', label: 'Productwissels', standaard: true },
      { id: 'klantenwissels', label: 'Klantenwissels', standaard: true },
      { id: 'verlengingen', label: 'Contractverlengingen', standaard: true },
    ],
  },
];

// Rollen die altijd ALLE subpagina's zien — ook nieuwe, zonder dat iemand iets
// aanvinkt. Voor hen toont het formulier de vinkjes vergrendeld.
export const SUBPAGE_FULL_ACCESS_ROLES = new Set(['admin', 'et_dossier']);

export const subPageKey = (moduleId, pageId) => `${moduleId}:${pageId}`;

export const SUBPAGE_IDS = SUBPAGE_CATALOG.flatMap((m) => m.pages.map((p) => subPageKey(m.module, p.id)));

// Energielabel waar een subpagina exclusief bij hoort (subpagina-id → label).
// Alleen de commissietabs staan hierin; zonder `energielabel` hoort een subpagina
// bij elk label. SPIEGELT de backend.
// Subpagina's die ALLEEN voor bepaalde rollen bestaan (subpagina-id → rollen).
// SPIEGELT de backend.
export const SUBPAGE_ROLES_BY_ID = new Map(
  SUBPAGE_CATALOG.flatMap((m) =>
    m.pages.filter((p) => Array.isArray(p.alleenRollen)).map((p) => [subPageKey(m.module, p.id), p.alleenRollen])
  )
);
export function subPageAllowedForRole(subpageId, role) {
  const rollen = SUBPAGE_ROLES_BY_ID.get(subpageId);
  return !rollen || rollen.includes(role);
}

export const SUBPAGE_LABEL_BY_ID = new Map(
  SUBPAGE_CATALOG.flatMap((m) =>
    m.pages.filter((p) => p.energielabel).map((p) => [subPageKey(m.module, p.id), p.energielabel])
  )
);

// Mag deze subpagina toegekend worden aan een gebruiker met dit energielabel?
// Labelvrije pagina's altijd; een labelgebonden pagina enkel bij precies dat
// label — elders bestaat de tab niet, dus is het vinkje zinloos.
export function subPageAllowedForLabel(subpageId, energyLabel) {
  const nodig = SUBPAGE_LABEL_BY_ID.get(subpageId);
  return !nodig || nodig === energyLabel;
}

// De basisset (`standaard: true`) — wat een nieuwe niet-admin meekrijgt.
export const DEFAULT_PARTNER_SUBPAGES = SUBPAGE_CATALOG.flatMap((m) =>
  m.pages.filter((p) => p.standaard).map((p) => subPageKey(m.module, p.id))
);

// Standaard toegekende subpagina's bij aanmaak, per rol (spiegelt de backend).
// Naast de basisset (`standaard: true`) krijgt een rol ook de pagina's die voor
// hém standaard aan staan (`standaardRollen`) — zo staat een tab bij een master
// meteen aan en bij een teamleader/verkoper uit, zonder dat ze onbereikbaar is.
//
// Een LABELGEBONDEN subpagina (`energielabel`) komt hier NOOIT in: die volgt het
// energielabel van de gebruiker, niet zijn rol, en staat bij het aanmaken dus
// altijd uit — behalve bij admin/ET-dossierbeheerder, die alles krijgen.
export function defaultSubPagesForRole(role) {
  if (SUBPAGE_FULL_ACCESS_ROLES.has(role)) return [...SUBPAGE_IDS];
  const extra = SUBPAGE_CATALOG.flatMap((m) =>
    m.pages.filter((p) => !p.standaard && !p.energielabel
      && Array.isArray(p.standaardRollen) && p.standaardRollen.includes(role))
      .map((p) => subPageKey(m.module, p.id))
  );
  return [...DEFAULT_PARTNER_SUBPAGES, ...extra];
}

// Set van toegekende subpagina's voor deze gebruiker, of `null` wanneer er geen
// beperking geldt. null = "alles tonen": admin en ET-dossierbeheerder, maar ook
// het geval dat de gebruiker (nog) niet geladen is — dan mag een tab niet
// zomaar verdwijnen.
export function subPageSetForUser(user) {
  if (!user || SUBPAGE_FULL_ACCESS_ROLES.has(user.role)) return null;
  // Rolgebonden subpagina's (`alleenRollen`) tellen alleen voor die rollen, wat
  // er ook in het profiel staat. De server filtert ze al; dit is het vangnet.
  return new Set((user.subPages || []).filter((id) => subPageAllowedForRole(id, user.role)));
}

// De tab-ids die voor deze module configureerbaar zijn. Een tab die hier niet in
// staat, blijft ongemoeid: die heeft dan zijn eigen zichtbaarheidsregel in de
// pagina zelf.
export function configureerbareTabIds(moduleId) {
  const mod = SUBPAGE_CATALOG.find((m) => m.module === moduleId);
  return new Set(mod ? mod.pages.map((p) => p.id) : []);
}

// Filtert een tabdefinitie-lijst (`[{ id, label, ... }]`) op wat deze gebruiker
// mag zien. `toegekend` = de Set uit subPageSetForUser (null → alles tonen).
export function filterSubPages(moduleId, tabs, toegekend) {
  if (!toegekend) return tabs;
  const configureerbaar = configureerbareTabIds(moduleId);
  return tabs.filter((t) => !configureerbaar.has(t.id) || toegekend.has(subPageKey(moduleId, t.id)));
}
